import React, { useState, useEffect, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";
import {
  Layers, Plus, Trash2, Edit3, X, Save, Image as ImageIcon,
  Eye, EyeOff, ArrowUp, ArrowDown, Film, Printer,
} from "lucide-react";
import { Music, Youtube } from "lucide-react";

function detectMusicKind(url) {
  const u = (url || "").trim();
  if (!u) return null;
  if (/spotify\.com|spotify:/i.test(u)) return "spotify";
  if (/youtu\.?be|youtube\.com/i.test(u)) return "youtube";
  if (/\.(mp3|ogg|wav|m4a|aac)($|\?)/i.test(u)) return "audio";
  return "unknown";
}

function MusicUrlField({ label, value, onChange, testid }) {
  const kind = detectMusicKind(value);
  const [showSuggestions, setShowSuggestions] = useState(false);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398] flex items-center gap-1.5">
        <Music size={11} className="text-[#D4AF37]" /> {label}
      </label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        placeholder="Spotify-Playlist oder YouTube-URL"
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs font-mono focus:border-[#D4AF37] outline-none" />
      <div className="mt-1 flex items-center gap-2 flex-wrap">
        {kind && (
          <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${
            kind === "spotify" ? "bg-[#1DB954]/15 text-[#1DB954]" :
            kind === "youtube" ? "bg-[#FF0000]/15 text-[#FF6B6B]" :
            kind === "audio"   ? "bg-[#D4AF37]/15 text-[#D4AF37]" :
                                "bg-[#575148]/15 text-[#575148]"
          }`}>
            {kind === "spotify" ? "🎵 Spotify" : kind === "youtube" ? <><Youtube size={10}/> YouTube</> : kind === "audio" ? "MP3" : "Unbekannt"}
          </div>
        )}
        <button type="button" onClick={() => setShowSuggestions((v) => !v)} data-testid={`${testid}-suggest-toggle`}
          className="text-[10px] uppercase tracking-widest text-[#1DB954] hover:text-[#EEDC82] inline-flex items-center gap-1">
          🎵 {showSuggestions ? "Vorschläge ausblenden" : "Passende Playlist vorschlagen"}
        </button>
      </div>
      {showSuggestions && (
        <div className="mt-2 p-2 bg-[#050505] border border-[#1DB954]/30 rounded space-y-2" data-testid={`${testid}-suggestions`}>
          {PLAYLIST_MOODS.map((mood) => (
            <div key={mood.name}>
              <div className="text-[9px] uppercase tracking-[0.32em] text-[#A8A398] mb-1">{mood.emoji} {mood.name}</div>
              <div className="flex flex-wrap gap-1">
                {mood.playlists.map((p) => (
                  <button key={p.url} onClick={() => onChange(p.url)} type="button"
                    data-testid={`${testid}-preset-${p.key}`}
                    className="px-2 py-1 text-[10px] rounded border border-[#1DB954]/30 hover:bg-[#1DB954]/10 text-[#EEDC82] hover:text-[#1DB954] transitions-safe">
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className="text-[9px] text-[#575148] pt-1 border-t border-[#1A1A1A]">
            Klicke auf einen Vorschlag zum Übernehmen · oder eigene Spotify-URL einfügen.
          </div>
        </div>
      )}
    </div>
  );
}

// Kuratierte Spotify-Playlists für typische Kino-Stimmungen
const PLAYLIST_MOODS = [
  { name: "Epic · Saga · Fantasy", emoji: "🗡️", playlists: [
    { key: "howard-shore", label: "Herr der Ringe · Score", url: "https://open.spotify.com/album/1BCTOo7BdcAj2mvGtsRMc0" },
    { key: "epic-orchestral", label: "Epic Orchestral", url: "https://open.spotify.com/playlist/37i9dQZF1DWZjqoI9tHFwG" },
    { key: "star-wars", label: "Star Wars · John Williams", url: "https://open.spotify.com/artist/1acQlS8lqAJPxu4bMbqXCk" },
    { key: "avengers", label: "Marvel · Avengers", url: "https://open.spotify.com/playlist/37i9dQZF1DX2VbwlgLLopO" },
  ]},
  { name: "Horror · Thriller · Dark", emoji: "🖤", playlists: [
    { key: "horror-score", label: "Horror Movie Scores", url: "https://open.spotify.com/playlist/37i9dQZF1DX8FwnYE6PRcL" },
    { key: "dark-ambient", label: "Dark Ambient", url: "https://open.spotify.com/playlist/37i9dQZF1DX2pSTOxoPbx9" },
    { key: "halloween", label: "Halloween Party", url: "https://open.spotify.com/playlist/37i9dQZF1DWUDcbsuBhKb0" },
  ]},
  { name: "Romance · Noir · Sensual", emoji: "🌹", playlists: [
    { key: "fifty-shades", label: "Fifty Shades OST", url: "https://open.spotify.com/album/47OKm7ZLTPhWMYaNvxJPWc" },
    { key: "romance", label: "Romantic Cinema", url: "https://open.spotify.com/playlist/37i9dQZF1DWXbLOeOIhbc5" },
    { key: "jazz-noir", label: "Jazz Noir", url: "https://open.spotify.com/playlist/37i9dQZF1DXbITWG1ZJKYt" },
    { key: "chill-lounge", label: "Cinema Lounge", url: "https://open.spotify.com/playlist/37i9dQZF1DX4WYpdgoIcn6" },
  ]},
  { name: "Family · Disney · Animation", emoji: "🎈", playlists: [
    { key: "disney", label: "Disney Hits", url: "https://open.spotify.com/playlist/37i9dQZF1DX8C9xQcOrE6T" },
    { key: "pixar", label: "Pixar Soundtracks", url: "https://open.spotify.com/playlist/37i9dQZF1DX6xOPeSOGone" },
    { key: "family-fun", label: "Family Fun", url: "https://open.spotify.com/playlist/37i9dQZF1DX8ymr6UES7vc" },
  ]},
  { name: "Sci-Fi · Retro · 80s", emoji: "🚀", playlists: [
    { key: "synthwave", label: "Synthwave / Retrowave", url: "https://open.spotify.com/playlist/37i9dQZF1DX0r3x8OtiwEM" },
    { key: "80s-movies", label: "80s Movie Nights", url: "https://open.spotify.com/playlist/37i9dQZF1DX4o1oenSJRJd" },
    { key: "blade-runner", label: "Sci-Fi Atmospheres", url: "https://open.spotify.com/playlist/37i9dQZF1DXcdQrCbG0OKB" },
  ]},
  { name: "Christmas · Festive", emoji: "🎄", playlists: [
    { key: "christmas", label: "Christmas Hits", url: "https://open.spotify.com/playlist/37i9dQZF1DX0Yxoavh5qJV" },
    { key: "christmas-jazz", label: "Christmas Jazz", url: "https://open.spotify.com/playlist/37i9dQZF1DX4ANONkOtcRy" },
  ]},
];



export default function Filmreihen() {
  const [rows, setRows] = useState([]);
  const [movies, setMovies] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/series"), api.get("/movies")]);
      setRows(a.data); setMovies(b.data);
    } catch { toast.error("Filmreihen konnten nicht geladen werden"); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Filmreihe wirklich löschen?")) return;
    try { await api.delete(`/series/${id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Fehler"); }
  };

  const toggleLanding = async (r) => {
    try {
      await api.patch(`/series/${r.id}`, { ...r, show_on_landing: !r.show_on_landing });
      load();
    } catch { toast.error("Fehler"); }
  };

  const move = async (r, dir) => {
    const sorted = [...rows].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const idx = sorted.findIndex((x) => x.id === r.id);
    const target = idx + dir;
    if (target < 0 || target >= sorted.length) return;
    const swap = sorted[target];
    try {
      await Promise.all([
        api.patch(`/series/${r.id}`, { ...r, display_order: swap.display_order || target }),
        api.patch(`/series/${swap.id}`, { ...swap, display_order: r.display_order || idx }),
      ]);
      load();
    } catch { toast.error("Fehler"); }
  };

  const sorted = useMemo(() => [...rows].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)), [rows]);

  return (
    <div className="space-y-6" data-testid="filmreihen-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Layers size={22} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-3xl">Filmreihen</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">Marvel, Star Wars, Herr der Ringe · mit eigenem Poster · optional öffentlich auf der Startseite</div>
          </div>
        </div>
        <button onClick={() => { setEdit(null); setOpen(true); }} data-testid="filmreihen-new"
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-5 py-2 rounded text-[11px] uppercase tracking-widest">
          <Plus size={14} /> Neue Filmreihe
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="filmreihen-grid">
        {sorted.length === 0 && (
          <div className="col-span-full text-center py-16 text-[#575148] border border-dashed border-[#2A2A2A] rounded-lg">
            <Layers size={32} className="mx-auto mb-3 opacity-40" />
            Noch keine Filmreihe angelegt.<br />
            <span className="text-xs">Beginne z.B. mit &bdquo;Marvel Cinematic Universe&ldquo; oder &bdquo;Herr der Ringe Trilogie&ldquo;.</span>
          </div>
        )}
        {sorted.map((r, i) => (
          <div key={r.id} data-testid={`filmreihe-${r.id}`}
            className="card-lux overflow-hidden group border" style={{ borderColor: r.color || "#2A2A2A" }}>
            <div className="relative aspect-[16/9] bg-[#050505]">
              {(r.backdrop_url || r.poster_url) ? (
                <img src={r.backdrop_url || r.poster_url} alt={r.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#575148]">
                  <ImageIcon size={38} className="opacity-30" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: r.color || "#D4AF37" }}>Filmreihe</div>
                <h3 className="font-heading text-2xl text-[#F5F0E6] mt-1">{r.name}</h3>
                {r.subtitle && <div className="text-xs text-[#A8A398] mt-0.5">{r.subtitle}</div>}
              </div>
              {r.show_on_landing && (
                <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#D4AF37] text-black text-[9px] uppercase tracking-widest px-2 py-1 rounded font-heading">
                  <Eye size={10} /> Öffentlich
                </div>
              )}
            </div>
            <div className="p-4 space-y-3">
              {r.description && <div className="text-xs text-[#A8A398] line-clamp-2">{r.description}</div>}
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#575148]">
                <Film size={11} /> {(r.movie_ids || []).length} Filme
              </div>
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-[#1A1A1A]">
                <button onClick={() => toggleLanding(r)} data-testid={`filmreihe-toggle-landing-${r.id}`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-[10px] uppercase tracking-widest border ${
                    r.show_on_landing ? "border-[#8B7328] text-[#EEDC82]" : "border-[#2A2A2A] text-[#575148] hover:text-[#EEDC82] hover:border-[#8B7328]"
                  }`}>
                  {r.show_on_landing ? <><Eye size={11} /> Sichtbar</> : <><EyeOff size={11} /> Verborgen</>}
                </button>
                <button onClick={() => move(r, -1)} disabled={i === 0} data-testid={`filmreihe-up-${r.id}`}
                  className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37] disabled:opacity-30"><ArrowUp size={11} /></button>
                <button onClick={() => move(r, +1)} disabled={i === sorted.length - 1} data-testid={`filmreihe-down-${r.id}`}
                  className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37] disabled:opacity-30"><ArrowDown size={11} /></button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/series/${r.id}/poster.pdf`} target="_blank" rel="noreferrer"
                  onClick={(e) => { e.preventDefault(); openPdf(`/admin/series/${r.id}/poster.pdf`); }}
                  data-testid={`filmreihe-poster-${r.id}`}
                  className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] rounded hover:bg-[#D4AF37]/10 text-[10px] uppercase tracking-widest">
                  <Printer size={11} /> Plakat A3
                </a>
                <button onClick={() => { setEdit(r); setOpen(true); }} data-testid={`filmreihe-edit-${r.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#2A2A2A] text-[#A8A398] rounded hover:border-[#D4AF37] hover:text-[#D4AF37] text-[10px] uppercase tracking-widest">
                  <Edit3 size={11} /> Bearbeiten
                </button>
                <button onClick={() => remove(r.id)} data-testid={`filmreihe-del-${r.id}`}
                  className="p-1.5 border border-[#7A1921]/40 text-[#EE9CA1] rounded hover:bg-[#7A1921]/20"><Trash2 size={11} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && <FilmreiheDialog initial={edit} movies={movies} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function FilmreiheDialog({ initial, movies, onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", subtitle: "", description: "", poster_url: "", backdrop_url: "",
    color: "#D4AF37", movie_ids: [], show_on_landing: true, display_order: 0,
    marathon_price: null, foyer_music_url: "", wall_music_url: "",
    pre_foyer_wall_url: "", pre_foyer_wall_duration_min: 0,
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (file, kind) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Bild zu groß (max. 8 MB)");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const full = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      upd(kind)(full);
      toast.success(kind === "poster_url" ? "Poster hochgeladen" : "Backdrop hochgeladen");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload fehlgeschlagen"); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name eingeben");
    setBusy(true);
    try {
      if (initial?.id) await api.patch(`/series/${initial.id}`, form);
      else await api.post("/series", form);
      toast.success("Filmreihe gespeichert");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler");
    } finally { setBusy(false); }
  };

  const toggleMovie = (mid) => {
    setForm((f) => ({
      ...f,
      movie_ids: f.movie_ids.includes(mid) ? f.movie_ids.filter((x) => x !== mid) : [...f.movie_ids, mid],
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-3xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-4 sm:my-8" onClick={(e) => e.stopPropagation()} data-testid="filmreihe-dialog">
        <div className="flex items-center gap-3 px-6 py-4 border-b border-[#1A1A1A]">
          <Layers size={20} className="text-[#D4AF37]" />
          <div>
            <div className="font-heading text-xl text-[#D4AF37]">{initial ? "Filmreihe bearbeiten" : "Neue Filmreihe"}</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398]">Poster · Filme · Landing-Anzeige</div>
          </div>
          <button onClick={onClose} className="ml-auto"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Name *</label>
              <input value={form.name} onChange={(e) => upd("name")(e.target.value)} data-testid="filmreihe-name"
                placeholder="Marvel Cinematic Universe"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Untertitel</label>
              <input value={form.subtitle || ""} onChange={(e) => upd("subtitle")(e.target.value)} data-testid="filmreihe-subtitle"
                placeholder="Phase 4 & 5 · Multiverse Saga"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none" />
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Beschreibung</label>
            <textarea rows={2} value={form.description || ""} onChange={(e) => upd("description")(e.target.value)} data-testid="filmreihe-description"
              placeholder="Erlebe das komplette MCU chronologisch in unserem Kino …"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImageUpload label="Poster (Hochformat)" value={form.poster_url} onChange={upd("poster_url")}
              onUpload={(f) => uploadImage(f, "poster_url")} aspect="2/3" testid="filmreihe-poster" />
            <ImageUpload label="Backdrop (Landscape)" value={form.backdrop_url} onChange={upd("backdrop_url")}
              onUpload={(f) => uploadImage(f, "backdrop_url")} aspect="16/9" testid="filmreihe-backdrop" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_120px] gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Filme in dieser Reihe</label>
              <div className="mt-1 max-h-48 overflow-y-auto bg-[#050505] border border-[#2A2A2A] rounded p-2 space-y-1">
                {movies.map((m) => {
                  const on = form.movie_ids.includes(m.id);
                  return (
                    <label key={m.id} className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transitions-safe ${on ? "bg-[#D4AF37]/10 border border-[#D4AF37]/40" : "hover:bg-[#1A1A1A]/60 border border-transparent"}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleMovie(m.id)}
                        data-testid={`filmreihe-movie-${m.id}`} className="accent-[#D4AF37]" />
                      {m.poster_url && <img src={m.poster_url} alt="" className="w-6 h-9 object-cover rounded" />}
                      <span className="text-sm text-[#F5F0E6] flex-1">{m.title_de}</span>
                      {m.year && <span className="text-[10px] font-mono text-[#575148]">{m.year}</span>}
                    </label>
                  );
                })}
                {movies.length === 0 && <div className="text-center text-[#575148] text-xs py-4">Keine Filme in der Datenbank</div>}
              </div>
              <div className="mt-1 text-[10px] text-[#575148]">{form.movie_ids.length} ausgewählt</div>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Farbe</label>
                <input type="color" value={form.color || "#D4AF37"} onChange={(e) => upd("color")(e.target.value)}
                  data-testid="filmreihe-color"
                  className="w-full h-10 mt-1 rounded bg-transparent border border-[#2A2A2A] cursor-pointer" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Reihenfolge</label>
                <input type="number" value={form.display_order || 0} onChange={(e) => upd("display_order")(Number(e.target.value) || 0)}
                  data-testid="filmreihe-order"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none font-mono" />
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm border-t border-[#1A1A1A] pt-4">
            <input type="checkbox" checked={!!form.show_on_landing} onChange={(e) => upd("show_on_landing")(e.target.checked)}
              data-testid="filmreihe-show-landing" className="accent-[#D4AF37]" />
            <span>
              <b className="text-[#EEDC82]">Auf der Startseite anzeigen</b>
              <div className="text-[10px] text-[#575148] mt-0.5">Diese Filmreihe ist für Besucher der Kino-Webseite sichtbar (mit Poster + Backdrop).</div>
            </span>
          </label>

          <div className="border-t border-[#1A1A1A] pt-4 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Marathon-Paket &amp; Musik</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Marathon-Fixpreis (€) · optional</label>
                <input type="number" step="0.5" min="0" value={form.marathon_price ?? ""} onChange={(e) => upd("marathon_price")(e.target.value === "" ? null : parseFloat(e.target.value))}
                  data-testid="filmreihe-marathon-price"
                  placeholder="z.B. 15 · alle 3 Filme zusammen"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none font-mono" />
                <div className="text-[10px] text-[#575148] mt-1">Erscheint als Badge auf der Landing (z.B. &bdquo;Marathon-Ticket 15 €&ldquo;).</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Wandbild-Dauer (Min)</label>
                <input type="number" min="0" max="15" value={form.pre_foyer_wall_duration_min || 0} onChange={(e) => upd("pre_foyer_wall_duration_min")(Math.max(0, Math.min(15, parseInt(e.target.value, 10) || 0)))}
                  data-testid="filmreihe-wall-duration"
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#D4AF37] outline-none font-mono" />
                <div className="text-[10px] text-[#575148] mt-1">0 = kein Wandbild vor dem Foyer.</div>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Wandbild-URL (vor dem Foyer)</label>
              <input value={form.pre_foyer_wall_url || ""} onChange={(e) => upd("pre_foyer_wall_url")(e.target.value)}
                data-testid="filmreihe-wall-url"
                placeholder="https://…/marvel-wall.jpg"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-xs font-mono focus:border-[#D4AF37] outline-none" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <MusicUrlField label="Wandbild-Musik" value={form.wall_music_url} onChange={upd("wall_music_url")} testid="filmreihe-wall-music" />
              <MusicUrlField label="Foyer-Musik" value={form.foyer_music_url} onChange={upd("foyer_music_url")} testid="filmreihe-foyer-music" />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[#1A1A1A]">
          <button onClick={onClose} className="py-2 px-4 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Abbrechen</button>
          <button onClick={save} disabled={busy || !form.name.trim()} data-testid="filmreihe-save"
            className="py-2 px-6 bg-[#D4AF37] hover:bg-[#EEDC82] text-black rounded text-[11px] uppercase tracking-widest disabled:opacity-40 inline-flex items-center gap-2">
            <Save size={12} /> {busy ? "…" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImageUpload({ label, value, onChange, onUpload, aspect, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <div className="mt-1 bg-[#050505] border border-[#2A2A2A] rounded overflow-hidden">
        <div className="relative bg-[#0A0A0A]" style={{ aspectRatio: aspect }}>
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#575148]"><ImageIcon size={32} className="opacity-40" /></div>
          )}
        </div>
        <div className="p-2 flex gap-2 items-center border-t border-[#1A1A1A]">
          <label className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 px-3 rounded border border-[#D4AF37]/50 text-[#D4AF37] cursor-pointer text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10" data-testid={`${testid}-upload`}>
            <ImageIcon size={11} /> Hochladen
            <input type="file" accept="image/*" className="hidden" onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
          {value && (
            <button onClick={() => onChange("")} className="p-1.5 border border-[#2A2A2A] rounded hover:border-[#7A1921]" data-testid={`${testid}-clear`}>
              <X size={11} />
            </button>
          )}
        </div>
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-url`}
          placeholder="oder URL einfügen"
          className="w-full bg-[#050505] border-t border-[#1A1A1A] px-3 py-2 text-[10px] font-mono focus:border-[#D4AF37] outline-none" />
      </div>
    </div>
  );
}
